<template>
  <header class="app-page-header" :class="headerClass">
    <nav
      v-if="displayBreadcrumbs.length"
      class="app-page-header__breadcrumb"
      aria-label="Breadcrumb"
    >
      <router-link to="/dashboard" class="app-page-header__breadcrumb-home" title="Dashboard">
        <i class="fas fa-home"></i>
      </router-link>
      <template v-for="(breadcrumb, index) in displayBreadcrumbs">
        <i
          :key="`separator-${index}`"
          class="fas fa-chevron-right app-page-header__breadcrumb-separator"
          aria-hidden="true"
        ></i>
        <router-link
          v-if="index < displayBreadcrumbs.length - 1 && breadcrumb.route"
          :key="`breadcrumb-${index}`"
          :to="breadcrumb.route"
          class="app-page-header__breadcrumb-link"
        >
          {{ breadcrumb.title }}
        </router-link>
        <span
          v-else-if="index < displayBreadcrumbs.length - 1"
          :key="`breadcrumb-${index}`"
          class="app-page-header__breadcrumb-parent"
        >
          {{ breadcrumb.title }}
        </span>
        <span
          v-else
          :key="`breadcrumb-${index}`"
          class="app-page-header__breadcrumb-current"
        >
          {{ breadcrumb.title }}
        </span>
      </template>
    </nav>

    <div class="app-page-header__main">
      <div class="app-page-header__identity">
        <div class="app-page-header__icon" aria-hidden="true">
          <slot name="icon">
            <i :class="icon"></i>
          </slot>
        </div>

        <div class="app-page-header__content">
          <h1 class="app-page-header__title">
            <slot name="title">{{ title }}</slot>
          </h1>
          <p v-if="subtitle || $slots.subtitle" class="app-page-header__subtitle">
            <slot name="subtitle">{{ subtitle }}</slot>
          </p>
        </div>
      </div>

      <div v-if="$slots.actions || (actionText && showAction)" class="app-page-header__actions">
        <slot name="actions">
          <b-button
            :variant="actionVariant"
            class="app-page-header__button"
            :disabled="actionDisabled"
            @click="$emit('action')"
          >
            <i v-if="actionIcon" :class="actionIcon"></i>
            <span>{{ actionText }}</span>
          </b-button>
        </slot>
      </div>
    </div>
  </header>
</template>

<script>
import { mapGetters } from "vuex";
import { resolveBreadcrumbs } from "@/core/services/breadcrumb-navigation";

export default {
  name: "PageHeader",
  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "far fa-file-alt",
    },
    actionText: {
      type: String,
      default: "",
    },
    actionIcon: {
      type: String,
      default: "fas fa-plus",
    },
    actionVariant: {
      type: String,
      default: "primary",
    },
    actionDisabled: {
      type: Boolean,
      default: false,
    },
    showAction: {
      type: Boolean,
      default: true,
    },
    headerClass: {
      type: [String, Array, Object],
      default: "",
    },
  },
  computed: {
    ...mapGetters(["breadcrumbs"]),
    displayBreadcrumbs() {
      return resolveBreadcrumbs(this.$route.path, this.breadcrumbs);
    },
  },
};
</script>

<style lang="scss" scoped>
.app-page-header {
  display: flex;
  flex-direction: column;
  gap: 13px;
  margin-bottom: 18px;
  padding: 16px 24px;
  background: #ffffff;
  border: 1px solid #e7edf4;
  border-radius: 16px;
  box-shadow: 0 10px 28px rgba(var(--skin-deep-rgb), 0.05);
}

.app-page-header__breadcrumb {
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.app-page-header__breadcrumb-home,
.app-page-header__breadcrumb-link,
.app-page-header__breadcrumb-parent,
.app-page-header__breadcrumb-current {
  min-width: 0;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}

.app-page-header__breadcrumb-home {
  flex: 0 0 auto;
  color: var(--skin-secondary);
}

.app-page-header__breadcrumb-link:hover {
  color: var(--skin-secondary);
}

.app-page-header__breadcrumb-link:focus-visible {
  color: var(--skin-secondary);
  outline: 2px solid rgba(var(--skin-secondary-rgb), 0.28);
  outline-offset: 3px;
  border-radius: 3px;
}

.app-page-header__breadcrumb-current {
  color: #1a2433;
  font-weight: 700;
}

.app-page-header__breadcrumb-separator {
  flex: 0 0 auto;
  margin: 0 9px;
  color: #b4bfcc !important;
  font-size: 8px;
}

.app-page-header__main {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.app-page-header__identity {
  min-width: 0;
  display: flex;
  align-items: center;
}

.app-page-header__icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  border-radius: 12px;
  background: rgba(var(--skin-secondary-rgb), 0.08);
  color: var(--skin-secondary);
  font-size: 18px;
}

.app-page-header__icon i {
  color: inherit !important;
}

.app-page-header__content {
  min-width: 0;
}

.app-page-header__title {
  margin: 0 0 2px;
  color: #1a2433;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
}

.app-page-header__subtitle {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}

.app-page-header__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.app-page-header__button {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0.55rem 1.5rem;
  border-radius: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.app-page-header__button i {
  margin: 0 !important;
}

::v-deep .app-page-header__actions > .btn {
  min-height: 40px;
  border-radius: 10px;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .app-page-header {
    margin-bottom: 14px;
    padding: 14px 16px;
  }

  .app-page-header__main {
    align-items: flex-start;
    flex-direction: column;
  }

  .app-page-header__actions,
  .app-page-header__button {
    width: 100%;
  }
}
</style>
